import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MailOpen, MessageSquareDot, Inbox, ChevronRight } from 'lucide-react';

import { useAuth } from '../../../hooks/useAuth';
import { isCeo } from '../../ceo-dashboard/model/ceoAccess';
import { useWebSocket } from '../../../hooks/useWebSocket';
import { useCompanyId } from '../../sale-order/hooks/useCompanyId';
import { APP_ROUTES } from '../../../app/routePaths';
import { useReplyInbox } from '../api/customerStatementApi';
import { customerStatementKeys } from '../queryKeys';
import { showDate } from './StatementCard';

const showDateTime = (iso: string) => `${showDate(iso)} ${iso.slice(11, 16)}`;

/**
 * The header icon for customers' replies to their statements.
 *
 * Shown only to roles on the server's notify list (the server answers
 * `allowed: false` for anyone else, so nothing is decided in the browser).
 * The badge is the number of replies nobody has opened. The list is fetched
 * over the REST API with the token; the WebSocket only carries "there are new
 * replies for company X", which triggers a refetch and a toast — the socket
 * endpoint is open and must never carry customer data.
 *
 * Clicking a reply opens the Send Statements workspace with that customer's
 * conversation already open; opening it marks the replies read, which clears
 * them from here.
 *
 * Not shown to the CEO (role 100 with permission 1; owner, 2026-10-17): no icon,
 * no inbox request, no "new replies" toast. Other notify-list roles keep it.
 */
export function StatementReplyBell() {
  const { roleId, permissionId } = useAuth();
  if (isCeo(roleId, permissionId)) return null;
  return <ReplyBell />;
}

function ReplyBell() {
  const auth = useAuth();
  const companyId = useCompanyId(auth.companyId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inbox = useReplyInbox(companyId);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const allowed = inbox.data?.allowed === true;
  const count = inbox.data?.count ?? 0;

  // Live signal from the mailbox reader: refetch, and say so.
  useWebSocket({
    topic: `/topic/statement-replies/${companyId ?? 0}`,
    enabled: allowed && !!companyId,
    onMessage: (msg: { count?: number } | string) => {
      void queryClient.invalidateQueries({ queryKey: customerStatementKeys.inbox(companyId ?? 0) });
      void queryClient.invalidateQueries({ queryKey: customerStatementKeys.replies(companyId ?? 0) });
      const n = typeof msg === 'object' && msg && typeof msg.count === 'number' ? msg.count : 1;
      toast(
        (t) => (
          <button
            type="button"
            className="text-left text-sm"
            onClick={() => {
              toast.dismiss(t.id);
              navigate(APP_ROUTES.customerStatementSend);
            }}
          >
            <b>{n} new statement repl{n === 1 ? 'y' : 'ies'}</b>
            <div className="text-xs text-slate-500">Click to open Send Statements</div>
          </button>
        ),
        { icon: '📬', duration: 8000 },
      );
    },
  });

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!allowed) return null;

  const openConversation = (customerId: number) => {
    setOpen(false);
    navigate(APP_ROUTES.customerStatementConversation(customerId));
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-1.5 rounded-lg transition-all duration-200"
        style={{ color: count ? 'var(--color-danger, #e11d48)' : 'var(--color-text-tertiary)' }}
        title={count ? `${count} unread customer repl${count === 1 ? 'y' : 'ies'}` : 'Customer replies to statements'}
        aria-label="Customer replies"
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--color-hover-overlay)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {count ? <MessageSquareDot className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
            style={{ backgroundColor: 'var(--color-danger, #e11d48)' }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-96 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <Inbox className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-black uppercase tracking-wide text-slate-600">Customer replies</span>
            <span className="ml-auto text-[11px] text-slate-500">{count ? `${count} unread` : 'nothing unread'}</span>
          </div>
          <ul className="max-h-[60vh] overflow-y-auto">
            {inbox.data?.items.map((item) => (
              <li key={item.replyId}>
                <button
                  type="button"
                  onClick={() => openConversation(item.customerId)}
                  className="flex w-full items-start gap-2 border-b border-slate-50 px-3 py-2 text-left transition hover:bg-blue-50"
                >
                  <MessageSquareDot className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs font-bold text-slate-800">{item.customerName}</span>
                      <span className="shrink-0 text-[10px] tabular-nums text-slate-400">{showDateTime(item.receivedAt)}</span>
                    </span>
                    <span className="block truncate text-[11px] text-slate-600">
                      {item.fromName || item.fromAddress}
                      {item.fromName && <span className="text-slate-400"> · {item.fromAddress}</span>}
                    </span>
                    {item.statementSubject && (
                      <span className="mt-0.5 block truncate text-[11px] text-blue-700" title={item.statementSubject}>
                        ↩ {item.statementSubject}{item.statementSentAt ? ` · sent ${showDate(item.statementSentAt)}` : ''}
                      </span>
                    )}
                    {item.snippet && <span className="mt-0.5 block truncate text-[11px] italic text-slate-500">“{item.snippet}”</span>}
                  </span>
                  <ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-slate-300" />
                </button>
              </li>
            ))}
            {inbox.data && inbox.data.items.length === 0 && (
              <li className="px-3 py-6 text-center text-xs text-slate-500">No unread replies. New ones appear here the moment the mailbox reader finds them.</li>
            )}
          </ul>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate(APP_ROUTES.customerStatementSend);
            }}
            className="flex w-full items-center justify-center gap-1.5 border-t border-slate-100 bg-slate-50 px-3 py-2 text-xs font-bold text-blue-700 hover:bg-blue-50"
          >
            Open Send Statements <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
